
import Vue from 'vue';
import { Component, Emit, InjectReactive, Model, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ResetButton from '@/components/buttons/ResetButton.vue';
import FilterBarProviderSeo from '@/components/filter-bar/seo/FilterBarProviderSeo.vue';
import CompetitionEventsItemOld from '@/components/sports/common/overview/calendar/CompetitionEventsItemOld.vue';
import DateChooser from '@/components/sports/common/overview/calendar/DateChooser.vue';
import EmptyCalendarOld from '@/components/sports/common/overview/calendar/EmptyCalendarOld.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { shiftDateWithTimezone } from '@/helpers/date-helper';

import { ObjectType } from '@/@types/graphql-types';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { SportEventBucketsEdgeV2, SportType } from '@/@types/graphql-types';
import AdUnit from '@/components/AdUnit.vue';
import type { WebLocale } from '@/enums/web-locale';
import { LocaleResponse } from '@/helpers/locale-helper';
import type Provider from '@/interfaces/provider';
import { SportPageType } from '@/interfaces/sport-graphql';
import { GetSportProvidersByShortNamesQuery } from '@/pages/graphql/queries/GetSportProvidersByShortNames.query';
import type { FilterCollection } from '@/stores/filter.store';
import type { UserSettings } from '@/stores/user.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'CompetitionEventsCalendarOld',
	components: {
		BasicButton,
		DateChooser,
		FilterBarProviderSeo,
		ResetButton,
		EmptyCalendarOld,
		CompetitionEventsItemOld,
		AdUnit,
	},
})
export default class CompetitionEventsCalendarOld extends Vue {
	@Prop({ required: true }) competitions: SportEventBucketsEdgeV2[];
	@Prop({ required: true }) isDataLoading: boolean;
	@Prop({ default: false }) hasLoadMore: boolean;
	@Prop({ required: true }) sportType: SportType;
	@Prop() sportPageType: SportPageType;

	@Model('date:change', { type: Date }) readonly displayDate!: Date;

	@InjectReactive() sportProvidersByShortName: GetSportProvidersByShortNamesQuery['packages'];

	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Language.State webLocale: WebLocale;
	@Language.Getter localeObject: LocaleResponse;

	@Filter.Getter currentFilters: FilterCollection;

	@User.State settings: UserSettings;

	get providers() {
		return this.sportProvidersByShortName
			.map(({ shortName }) => this.providersByShortName[shortName])
			.filter(provider => provider != null);
	}

	get sportContext() {
		return new SnowplowSportContext(this.sportType, ObjectType.SportCompetition);
	}

	@Emit('loadMore')
	handleLoadMoreClick() {}

	@Emit('date:change')
	handleReturn() {
		this.trackDateFilter('reset');
		return shiftDateWithTimezone(this.localeObject.timezone, 0);
	}

	@Emit('date:change')
	handleNext() {
		this.trackDateFilter('right');
		return shiftDateWithTimezone(this.localeObject.timezone, 1, this.displayDate);
	}

	@Emit('date:change')
	handlePrev() {
		this.trackDateFilter('left');
		return shiftDateWithTimezone(this.localeObject.timezone, -1, this.displayDate);
	}

	@Emit('date:change')
	handleDate(shift: number) {
		this.trackDateFilter('date');
		return shiftDateWithTimezone(this.localeObject.timezone, shift, this.displayDate);
	}

	private trackDateFilter(label: 'reset' | 'right' | 'left' | 'date') {
		TrackingHelper.trackEvent('userinteraction', { action: 'date_filter_clicked', label }, [this.sportContext]);
	}

	toggleProvider(data: { filterName: keyof FilterCollection; value: any; selectedState: 1 | 0 }) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'filter_toggled',
				label: data.filterName,
				property: data.value ?? 'all',
				value: data.selectedState,
			},
			[this.sportContext]
		);
	}
}
